import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { useForm } from '@tanstack/react-form'

import axios, { pluckData, pluckError } from 'src/utilities/http-client'
import { CancelIcon, EditIcon, SubmitIcon } from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'

import {
  fetchCourses,
  fetchCredentialRecertificationCycles,
  fetchCredentials,
  fetchLicenseRecertificationCycles,
  fetchLicenses,
  fetchNREMTLicense,
  fetchNREMTRecertificationCycles,
  fetchRecertificationCycleTemplateDetail,
  fetchUnstartedComplianceRecertItems,
  selectCredentials,
  selectCredentialsStatus,
  selectCredentialsV2,
  selectLicenses,
  selectLicensesStatus,
  selectNREMTLicense,
  selectNREMTLicenseStatus,
  selectCourses,
  selectCoursesStatus,
  selectRecertificationCycleTemplateDetail,
  selectRecertificationCycleTemplateDetailStatus,
} from 'src/v3/profile/profile.slice'
import { selectUseCompliance } from 'src/v3/settings/settings.slice'
import {
  Box,
  Button,
  capitalize,
  Card,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import {
  CREDENTIAL_TYPES,
  RecertCycleType,
  RecertificationCycle,
} from 'src/v3/core/types'
import { licenseTypeDict } from 'src/v3/profile/v4/recertification-cycle'
import { LicenseForm } from 'src/v3/profile/v4/licenses/components/license-form'
import { NREMTLicenseForm } from 'src/v3/profile/v4/licenses/components/nremt-form'
import { CredentialForm } from 'src/v3/profile/v4/credentials/credential-form'

type CreateCycleFormProps = {
  open: boolean
  setOpen: (status: boolean) => void
  initialCycleType?: RecertCycleType
  templateID?: string
}

export let CreateCycleForm = ({
  open,
  setOpen,
  initialCycleType,
  templateID,
}: CreateCycleFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let useCompliance = useAppSelector(selectUseCompliance)
  let { showError, NotificationComponent } = useNotifications()

  let licenses = useAppSelector(selectLicenses)
  let licensesStatus = useAppSelector(selectLicensesStatus)
  let credentials = useAppSelector(selectCredentials)
  let credentialsStatus = useAppSelector(selectCredentialsStatus)
  let credentialsV2 = useAppSelector(selectCredentialsV2)
  let nremt = useAppSelector(selectNREMTLicense)
  let nremtStatus = useAppSelector(selectNREMTLicenseStatus)

  // The entity being fixed from the eligibility error, and the name it suggested when the edit
  // began. A cycle name still matching that suggestion is regenerated once the edit closes,
  // since it embeds the expiration date.
  let [editing, setEditing] = useState<
    { kind: RecertCycleType['kind']; id: string } | undefined
  >(undefined)
  let [pendingRename, setPendingRename] = useState<string | undefined>(
    undefined,
  )

  let [showTemplate, setShowTemplate] = useState(false)
  let templateDetail = useAppSelector(
    selectRecertificationCycleTemplateDetail(templateID || ''),
  )
  let templateDetailStatus = useAppSelector(
    selectRecertificationCycleTemplateDetailStatus(templateID || ''),
  )

  let sortedLicenses = [
    licenses.find((l) => l.displayRank === 'Primary'),
    licenses.find((l) => l.displayRank === 'Secondary'),
    ...licenses
      .filter((l) => l.displayRank === 'Additional')
      .toSorted((a, b) => a.state.localeCompare(b.state)),
  ].filter((l) => !!l)

  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)

  useAutoFetch(licensesStatus, fetchLicenses)
  useAutoFetch(credentialsStatus, fetchCredentials)
  useAutoFetch(nremtStatus, fetchNREMTLicense)
  useAutoFetch(coursesStatus, fetchCourses)

  let [saving, setSaving] = useState(false)

  let form = useForm({
    defaultValues: {
      cycleType: initialCycleType?.kind ?? 'license',
      id: 'unselected',
      name: '',
    },
    onSubmit: async ({ value, formApi }) => {
      setSaving(true)
      axios
        .post<RecertificationCycle>(
          `/api/profile/${value.cycleType}/recertification-cycle`,
          {
            id: value.id,
            name: value.name,
            templateID,
          },
        )
        .then(pluckData)
        .then((cycle) => {
          if (useCompliance) {
            axios
              .post('/api/profile/compliance/link-recertification', {
                id: cycle.id,
              })
              .then(() => dispatch(fetchUnstartedComplianceRecertItems()))
              .catch((err) =>
                showError?.(
                  pluckError(
                    err,
                    'Error linking recertification cycle to compliance',
                  ),
                ),
              )
          }
          ;(value.cycleType === 'license'
            ? dispatch(fetchLicenseRecertificationCycles())
            : value.cycleType === 'credential'
              ? dispatch(fetchCredentialRecertificationCycles())
              : dispatch(fetchNREMTRecertificationCycles())
          ).then(() => navigate(`/profile/recertification/cycle/${cycle.id}`))
        })
        .then(() => setOpen(false))
        .then(() => formApi.reset())
        .catch((err) => {
          showError?.(pluckError(err, 'Error Creating Recertification Cycle'))
        })
        .finally(() => setSaving(false))
    },
  })

  useEffect(() => {
    if (
      showTemplate &&
      templateID &&
      (!templateDetailStatus || templateDetailStatus === 'uninitialized')
    ) {
      dispatch(fetchRecertificationCycleTemplateDetail(templateID))
    }
  }, [showTemplate, templateID, templateDetailStatus])

  useEffect(() => {
    // Prefill only until something is selected: later refetches (e.g. after Edit) re-run this
    // effect and would otherwise overwrite a customized name.
    if (!initialCycleType || form.state.values.id !== 'unselected') return

    if (initialCycleType.kind === 'nremt') {
      if (!nremt) return
      form.setFieldValue('id', nremt.id)
      form.setFieldValue(
        'name',
        `NREMT ${'•'} ${nremt.type} ${'•'} Exp. ${dayjs(nremt.expiration).format('MM/DD/YYYY')}`,
      )
      return
    }

    if (initialCycleType.kind === 'license') {
      if (licensesStatus !== 'fulfilled') return
      let matching = sortedLicenses.filter(
        (l) =>
          l.type.toLowerCase() === initialCycleType.type.toLowerCase() &&
          l.state.toLowerCase() === initialCycleType.state.toLowerCase(),
      )
      if (matching.length !== 1) return
      let [license] = matching
      form.setFieldValue('id', license.id)
      form.setFieldValue(
        'name',
        `${license.state} ${'•'} ${license.type} ${'•'} Exp. ${dayjs(license.expiration).format('MM/DD/YYYY')}`,
      )
      return
    }

    if (initialCycleType.kind === 'credential') {
      if (credentialsStatus !== 'fulfilled') return
      let matching = credentials.filter(
        (c) =>
          c.credentialType.toLowerCase() ===
          initialCycleType.type.toLowerCase(),
      )
      if (matching.length !== 1) return
      let [credential] = matching
      form.setFieldValue('id', credential.id)
      form.setFieldValue(
        'name',
        `${credential.credentialType.toUpperCase()} ${'•'} ${credential.credentialIDNumber} ${'•'} Exp. ${dayjs(credential.expiration).format('MM/DD/YYYY')}`,
      )
    }
  }, [initialCycleType, nremt, licensesStatus, credentialsStatus])

  let suggestedCycleName = (
    cycleType: RecertCycleType['kind'],
    id: string,
  ): string | undefined => {
    if (cycleType === 'nremt') {
      return nremt?.id === id
        ? `NREMT ${'\u2022'} ${nremt.type} ${'\u2022'} Exp. ${dayjs(nremt.expiration).format('MM/DD/YYYY')}`
        : undefined
    }
    if (cycleType === 'license') {
      let license = licenses.find((x) => x.id === id)
      return license
        ? `${license.state} ${'\u2022'} ${license.type} ${'\u2022'} Exp. ${dayjs(license.expiration).format('MM/DD/YYYY')}`
        : undefined
    }
    let credential = credentials.find((x) => x.id === id)
    return credential
      ? `${credential.credentialType.toUpperCase()} ${'\u2022'} ${credential.credentialIDNumber} ${'\u2022'} Exp. ${dayjs(credential.expiration).format('MM/DD/YYYY')}`
      : undefined
  }

  // Mirrors the backend's eligibility checks, which otherwise surface only after submitting.
  let ineligibilityReason = (
    cycleType: RecertCycleType['kind'],
    id: string,
  ): string | undefined => {
    if (cycleType === 'nremt') {
      return nremt?.id === id && !nremt.issueDate
        ? 'Your NREMT needs an issue date before it can have a recertification cycle.'
        : undefined
    }
    if (cycleType === 'license') {
      let license = licenses.find((x) => x.id === id)
      return license && (!license.issueDate || !license.expiration)
        ? 'This license needs an issue date and expiration date before it can have a recertification cycle.'
        : undefined
    }
    let credential = credentialsV2.find((x) => x.id === id)
    return credential && !credential.issueDate
      ? 'This credential needs an issue date before it can have a recertification cycle.'
      : undefined
  }

  // The edit forms refetch the store before closing, so the refreshed entity is available here.
  useEffect(() => {
    if (editing || pendingRename === undefined) return
    let { cycleType, id, name } = form.state.values
    let suggestion = suggestedCycleName(cycleType, id)
    if (suggestion && name === pendingRename)
      form.setFieldValue('name', suggestion)
    setPendingRename(undefined)
  }, [editing, pendingRename, licenses, credentials, nremt])

  let startEditing = (cycleType: RecertCycleType['kind'], id: string) => {
    setPendingRename(suggestedCycleName(cycleType, id))
    setEditing({ kind: cycleType, id })
  }

  let handleOnClose = () => {
    form.reset()
    setOpen(false)
  }

  let labels: Record<string, string> = {
    license: 'License',
    credential: 'Credential',
    nremt: 'NREMT',
  }

  return (
    <Dialog open={open} onClose={handleOnClose}>
      <Paper sx={{ width: 'fit-content', px: 2, py: 1 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          Create New Cycle
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <form.Field
              name="cycleType"
              listeners={{
                onChange: () => {
                  form.setFieldValue('id', 'unselected')
                  form.setFieldValue('name', '')
                },
              }}
              children={(field) => (
                <FormControl>
                  <FormLabel>Cycle Type</FormLabel>
                  <RadioGroup
                    row
                    value={field.state.value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      field.handleChange(
                        e.target.value as RecertCycleType['kind'],
                      )
                    }
                  >
                    <FormControlLabel
                      value="nremt"
                      control={<Radio />}
                      label="NREMT"
                      disabled={
                        !!initialCycleType && initialCycleType.kind !== 'nremt'
                      }
                    />
                    <FormControlLabel
                      value="license"
                      control={<Radio />}
                      label="License"
                      disabled={
                        !!initialCycleType &&
                        initialCycleType.kind !== 'license'
                      }
                    />
                    <FormControlLabel
                      value="credential"
                      control={<Radio />}
                      label="Credential"
                      disabled={
                        !!initialCycleType &&
                        initialCycleType.kind !== 'credential'
                      }
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
            <form.Subscribe
              selector={(state) => state.values.cycleType}
              children={(cycleType) => (
                <form.Field
                  name="id"
                  validators={{
                    onSubmit: ({ value }) =>
                      value === 'unselected'
                        ? `${labels[cycleType]} is a required field`
                        : undefined,
                  }}
                  listeners={{
                    onChange: ({ value }) => {
                      let suggestion = suggestedCycleName(cycleType, value)
                      if (suggestion) form.setFieldValue('name', suggestion)
                    },
                  }}
                  children={(field) => {
                    let reason = ineligibilityReason(
                      cycleType,
                      field.state.value,
                    )
                    return (
                      <FormControl
                        fullWidth
                        error={field.state.meta.errors.length > 0 || !!reason}
                      >
                        <InputLabel id="cycle-entity-label">
                          {initialCycleType?.kind === 'license'
                            ? `${capitalize(initialCycleType.state)} ${licenseTypeDict[initialCycleType.type.toLowerCase()] ?? initialCycleType.type} License`
                            : initialCycleType?.kind === 'credential'
                              ? `${CREDENTIAL_TYPES.some((t) => t.toLowerCase() === initialCycleType.type.toLowerCase()) ? initialCycleType.type.toUpperCase() : initialCycleType.type} Credential`
                              : labels[cycleType]}
                        </InputLabel>
                        <Select
                          labelId="cycle-entity-label"
                          value={field.state.value}
                          label={
                            initialCycleType?.kind === 'license'
                              ? `${capitalize(initialCycleType.state)} ${licenseTypeDict[initialCycleType.type.toLowerCase()] ?? initialCycleType.type} License`
                              : initialCycleType?.kind === 'credential'
                                ? `${CREDENTIAL_TYPES.some((t) => t.toLowerCase() === initialCycleType.type.toLowerCase()) ? initialCycleType.type.toUpperCase() : initialCycleType.type} Credential`
                                : labels[cycleType]
                          }
                          onChange={(e) => field.handleChange(e.target.value)}
                          sx={{ bgcolor: 'background.paper' }}
                        >
                          <MenuItem value="unselected">Unselected</MenuItem>
                          {cycleType === 'license' &&
                            sortedLicenses
                              .filter((license) =>
                                initialCycleType?.kind === 'license'
                                  ? license.type.toLowerCase() ===
                                      initialCycleType.type.toLowerCase() &&
                                    license.state.toLowerCase() ===
                                      initialCycleType.state.toLowerCase()
                                  : true,
                              )
                              .map((license) => (
                                <MenuItem key={license.id} value={license.id}>
                                  {license.displayRank !== 'Additional' && (
                                    <Typography
                                      component="span"
                                      color={
                                        license.displayRank === 'Primary'
                                          ? 'primary'
                                          : 'secondary'
                                      }
                                    >
                                      *
                                    </Typography>
                                  )}
                                  {license.state} {'\u2022'} {license.type}
                                </MenuItem>
                              ))}
                          {cycleType === 'credential' &&
                            credentials
                              .toSorted((a, b) =>
                                a.credentialType.localeCompare(
                                  b.credentialType,
                                ),
                              )
                              .filter((credential) =>
                                initialCycleType?.kind === 'credential'
                                  ? credential.credentialType.toLowerCase() ===
                                    initialCycleType.type.toLowerCase()
                                  : true,
                              )
                              .map((credential) => (
                                <MenuItem
                                  key={credential.id}
                                  value={credential.id}
                                >
                                  {credential.credentialType.toUpperCase()}
                                  {' \u2022 '}
                                  {credential.credentialIDNumber}
                                </MenuItem>
                              ))}
                          {cycleType === 'nremt' && (
                            <MenuItem value={nremt?.id}>
                              {nremt?.type} {'\u2022'} {nremt?.nremtidNumber}
                            </MenuItem>
                          )}
                        </Select>
                        {field.state.meta.errors.length > 0 && (
                          <FormHelperText>
                            {field.state.meta.errors.join(', ')}
                          </FormHelperText>
                        )}
                        {reason && (
                          <>
                            <FormHelperText>{reason}</FormHelperText>
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() =>
                                startEditing(cycleType, field.state.value)
                              }
                              sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                            >
                              Edit {labels[cycleType]}
                            </Button>
                          </>
                        )}
                      </FormControl>
                    )
                  }}
                />
              )}
            />
            <form.Field
              name="name"
              validators={{
                onSubmit: ({ value }) =>
                  !value ? 'Name is a required field' : undefined,
              }}
              children={(field) => (
                <TextField
                  sx={{ bgcolor: 'background.paper', my: 0.5 }}
                  fullWidth
                  variant="outlined"
                  value={field.state.value}
                  label="Name"
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors.length > 0}
                  helperText={field.state.meta.errors.join(', ')}
                />
              )}
            />
            {templateID && (
              <Box>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => setShowTemplate((v) => !v)}
                >
                  {showTemplate ? 'Hide Template' : 'Show Template'}
                </Button>
                <Collapse in={showTemplate}>
                  {templateDetailStatus === 'pending' && (
                    <Typography color="text.secondary" sx={{ py: 1 }}>
                      Loading…
                    </Typography>
                  )}
                  {templateDetail && (
                    <Box
                      sx={{
                        mt: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                      }}
                    >
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {templateDetail.template.name}
                      </Typography>
                      {templateDetail.categories.map(
                        ({ category, courses: categoryCourses }) => (
                          <Card
                            key={category.id}
                            variant="outlined"
                            sx={{ p: 1.5, bgcolor: 'background.paperDark' }}
                          >
                            <Box
                              sx={{
                                pb: 0.5,
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Typography
                                sx={{ fontSize: 16, ml: 0.5, flexGrow: 1 }}
                              >
                                {category.name}
                              </Typography>
                              <Typography
                                color="text.secondary"
                                variant="body2"
                              >
                                {'•'}
                              </Typography>
                              <Typography
                                color="text.secondary"
                                variant="body2"
                              >
                                {category.hours} Hour
                                {category.hours === 1 ? '' : 's'} Required
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                mt: 0.5,
                                pl: 0.5,
                                color: 'text.secondary',
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: 1,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 'bold' }}
                              >
                                {categoryCourses.length === 0
                                  ? 'No Courses'
                                  : 'Course List'}
                              </Typography>
                            </Box>
                            {categoryCourses.length > 0 && (
                              <Box
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                {categoryCourses.map((courseData, i) => {
                                  let courseName =
                                    courses.find(
                                      (c) => c.id === courseData.courseID,
                                    )?.name ?? courseData.courseID
                                  return (
                                    <Box
                                      key={courseData.id}
                                      sx={{
                                        px: 1.5,
                                        py: 0.75,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        bgcolor: i % 2 ? 'divider' : undefined,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ flexGrow: 1 }}
                                      >
                                        {courseName}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ flexShrink: 0 }}
                                      >
                                        {courseData.hours} Hr
                                        {courseData.hours === 1 ? '' : 's'}
                                      </Typography>
                                    </Box>
                                  )
                                })}
                                <Divider />
                              </Box>
                            )}
                          </Card>
                        ),
                      )}
                    </Box>
                  )}
                </Collapse>
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: 1.5,
            }}
          >
            <form.Subscribe
              selector={(state) =>
                state.isValid &&
                !ineligibilityReason(state.values.cycleType, state.values.id)
              }
              children={(isValid) => (
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  sx={{
                    flex: '1 1 0px',
                  }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              color="error"
              onClick={handleOnClose}
              variant="contained"
              sx={{
                flex: '1 1 0px',
              }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
        {NotificationComponent}
      </Paper>
      {editing?.kind === 'license' && (
        <LicenseForm
          key={editing.id}
          license={licenses.find((l) => l.id === editing.id)}
          open
          setOpen={() => setEditing(undefined)}
          onError={(error) => showError?.(error)}
        />
      )}
      {editing?.kind === 'credential' && (
        <CredentialForm
          key={editing.id}
          credential={credentialsV2.find((c) => c.id === editing.id)}
          open
          setOpen={() => setEditing(undefined)}
          onError={(error) => showError?.(error)}
        />
      )}
      {editing?.kind === 'nremt' && (
        <NREMTLicenseForm
          nremtLicense={nremt}
          open
          setOpen={() => setEditing(undefined)}
          onError={(error) => showError?.(error)}
        />
      )}
    </Dialog>
  )
}
